import _ from "lodash";
import React, { useState, useRef } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import classNames from "classnames";

import classes from "./UnitFeed.scss";
import { useI18n } from "Hooks";
import UnitCard from "AppComponents/LEIntoUnitImport/components/UnitCard";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getUnitPlanFeedQueryFromCache } from "AppComponents/LEIntoUnitImport/modules/GraphqlHelpers";
import { getUnitPlanFeedQuery } from "AppComponents/LEIntoUnitImport/modules/Queries";
import withLoader from "UIComponents/GqlWithLoader";
import {
  getUnitPlanFeedList,
  getUnitPlanQueryVariables,
} from "AppComponents/LEIntoUnitImport/modules/Utils";
import { navigateGridWithArrowKeys } from "Utils/AccessibilityHelpers";
import { EmptyState } from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";

const UnitCards = ({ unitPlansList, selectedUnit, parentContainer }) => {
  const unitCardRefs = useRef({});

  let containerComputedStyle;
  if (parentContainer) {
    containerComputedStyle = window.getComputedStyle(parentContainer);
  }

  const columnsCount = containerComputedStyle?.gridTemplateColumns?.split(" ")
    ?.length; // Calculate the number of cards per row

  const getUnitCardId = index => unitPlansList[index]?.id;

  // Function to move focus based on key press
  const moveFocusToNextOrPrevFeedData = (event, index) => {
    navigateGridWithArrowKeys({
      event,
      index,
      columnsCount,
      itemsCount: _.size(unitPlansList),
      refList: unitCardRefs,
      getItemId: getUnitCardId,
    });
  };

  return unitPlansList.map((unitPlan, index) => {
    const isSelected = unitPlan.id === selectedUnit.id;
    return (
      <UnitCard
        key={unitPlan.id}
        unitPlanInfo={unitPlan}
        index={index}
        isSelected={isSelected}
        unitCardRefs={unitCardRefs}
        onKeyDown={e => moveFocusToNextOrPrevFeedData(e, index)}
      />
    );
  });
};

const UnitFeed = ({ unitPlansList = [], unitPlansCount, selectedUnit }) => {
  const { t } = useI18n();

  const [unitCardContainer, setUnitCardContainer] = useState(null);

  const showEmptyState = _.isEmpty(unitPlansList);

  const emptyStateComponent = (
    <div className={classes.emptyStateContainer}>
      <EmptyState
        illustration={NoSearchResultsIllustration}
        size="small"
        title={t("common:no_result_found")}
        subtitle={t("common:emptyText_search2")}
      />
    </div>
  );

  const feedComponent = (
    <>
      <div className={classNames("heading-6", classes.countText)}>
        {t("common:label_unit", {
          count: unitPlansCount,
        })}
      </div>
      <div
        className={classes.unitCardContainer}
        ref={ele => setUnitCardContainer(ele)}
      >
        <UnitCards
          unitPlansList={unitPlansList}
          selectedUnit={selectedUnit}
          parentContainer={unitCardContainer}
        />
      </div>
    </>
  );

  return (
    <div className={classes.listContainer}>
      {showEmptyState ? emptyStateComponent : feedComponent}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    LEIntoUnitImport: { gradeId, filters, selectedUnit },
  } = state;

  const { academicYears } = ownProps;

  const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
  const platformOrgId = platformUserInfo.org_id;

  const unitPlanFeedQueryVariables = getUnitPlanQueryVariables({
    orgId: platformOrgId,
    gradeId,
    filters,
    academicYears,
  });

  return {
    gradeId,
    platformOrgId,
    isData: true,
    isLoading: false,
    unitPlanFeedQueryVariables,
    selectedUnit,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getUnitPlanFeedQuery, {
    name: "getUnitPlanFeed",
    skip: ({ platformOrgId, gradeId }) => !platformOrgId || !gradeId,
    options: ({ unitPlanFeedQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: unitPlanFeedQueryVariables,
    }),
    props({
      getUnitPlanFeed,
      ownProps: { unitPlanFeedQueryVariables, isData, isLoading },
    }) {
      const queryData = getUnitPlanFeedQueryFromCache(
        unitPlanFeedQueryVariables
      );
      const unitPlans = _.get(queryData, "unitPlansV2", {});

      const unitPlansList = getUnitPlanFeedList(unitPlans);
      const unitPlansCount = _.size(unitPlansList);

      return {
        isData: !_.isEmpty(unitPlans) && isData,
        isLoading:
          isLoading || _.includes([1, 2], getUnitPlanFeed.networkStatus),
        unitPlansList,
        unitPlansCount,
      };
    },
  }),
  withLoader
)(UnitFeed);
